import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Layers, Plus, Search, Edit2, Trash2, CheckCircle2, 
  Sparkles, Calendar, FileText, Hash, ArrowRight, 
  HelpCircle, ShieldCheck, RefreshCw, X, Check, ArrowUpDown,
  Maximize2, Minimize2, Table, Copy, CheckCheck, AlertCircle,
  AlertTriangle, Award, Building2, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  fetchBatchMasters, 
  saveBatchMasterList,
  fetchBatchHistories, 
  generateBatchCode, 
  formatBrandCode, 
  getMonthLetter,
  MONTH_LETTERS,
  checkBrandCodeCollision,
  generateUniqueBrandCode
} from '@/lib/batchHelper';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';

const cleanVariantKey = (str = '') => {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '')
    .trim();
};

const ExpandedBrandModal = React.memo(({ brand, allRacikanDb, onClose, onUpdateVariants }) => {
  const [search, setSearch] = useState('');
  const [newVarName, setNewVarName] = useState('');
  const [editingVar, setEditingVar] = useState(null); // { index, name }

  const allVariants = brand.variants || [];

  // 1. Memoized missing variants check - calculated with normalized key matching
  const missingFromBatch = useMemo(() => {
    if (!allRacikanDb || allRacikanDb.length === 0 || !brand.brand_name) return [];
    const brandClean = cleanVariantKey(brand.brand_name);
    const brandLower = brand.brand_name.trim().toLowerCase();

    // Filter recipes for this brand (support both exact and cleaned comparison)
    const brandRecipes = allRacikanDb.filter(r => {
      const rBrand = (r.nama_brand || '').trim();
      return rBrand.toLowerCase() === brandLower || cleanVariantKey(rBrand) === brandClean;
    });

    const recipeNames = [...new Set(brandRecipes.map(r => (r.nama_varian || r.nama_variant || '').trim()).filter(Boolean))];

    const registeredCleanSet = new Set(allVariants.map(v => cleanVariantKey(v.name)).filter(Boolean));
    const registeredExactSet = new Set(allVariants.map(v => (v.name || '').trim().toLowerCase()).filter(Boolean));

    return recipeNames.filter(name => {
      const clean = cleanVariantKey(name);
      const lower = name.toLowerCase();
      return !registeredExactSet.has(lower) && !registeredCleanSet.has(clean);
    });
  }, [brand.brand_name, allVariants, allRacikanDb]);

  // 2. Memoized indexed variants for instant O(1) rendering and flexible search
  const filteredIndexedVariants = useMemo(() => {
    const list = allVariants.map((v, i) => ({ ...v, _index: i, _code: v.code || String(i + 1).padStart(2, '0') }));
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    const qClean = cleanVariantKey(search);
    return list.filter(v => {
      const name = (v.name || '').toLowerCase();
      const code = v._code;
      const cleanName = cleanVariantKey(v.name);
      return name.includes(q) || code.includes(q) || (qClean && cleanName.includes(qClean));
    });
  }, [allVariants, search]);

  const handleAddMissing = async (varName) => {
    const nextNum = allVariants.length + 1;
    const formattedCode = String(nextNum).padStart(2, '0');
    const updated = [...allVariants, { name: varName.trim(), code: formattedCode, registered_date: new Date().toISOString().slice(0, 10) }];
    await onUpdateVariants(brand.id, updated);
    toast.success(`Varian "${varName}" ditambahkan sebagai No. ${formattedCode}`);
  };

  const handleAddAll = async () => {
    let currentCount = allVariants.length;
    const newItems = missingFromBatch.map(varName => {
      currentCount++;
      return { 
        name: varName.trim(), 
        code: String(currentCount).padStart(2, '0'),
        registered_date: new Date().toISOString().slice(0, 10)
      };
    });
    const updated = [...allVariants, ...newItems];
    await onUpdateVariants(brand.id, updated);
    toast.success(`Berhasil menambahkan ${newItems.length} varian baru ke Brand ${brand.brand_name}!`);
  };

  const handleAddCustom = async () => {
    if (!newVarName.trim()) return;
    await handleAddMissing(newVarName);
    setNewVarName('');
  };

  const handleSaveEdit = async () => {
    if (!editingVar || !editingVar.name.trim()) return;
    const updated = [...allVariants];
    if (updated[editingVar.index]) {
      updated[editingVar.index] = { ...updated[editingVar.index], name: editingVar.name.trim() };
      await onUpdateVariants(brand.id, updated);
      setEditingVar(null);
    }
  };

  const handleDelete = async (idx) => {
    const varName = allVariants[idx]?.name || 'Varian';
    if (!window.confirm(`Hapus varian "${varName}"? Nomor urut berikutnya akan disesuaikan otomatis.`)) return;
    const remaining = allVariants.filter((_, i) => i !== idx).map((v, i) => ({
      ...v,
      code: String(i + 1).padStart(2, '0')
    }));
    await onUpdateVariants(brand.id, remaining);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-150">
      <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-[#E6D5B8] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#F9F5EC] via-[#F3EAD8] to-[#EFE2CA] p-5 border-b border-[#E6D5B8] flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-[#302621] text-[#D4AF37] flex items-center justify-center font-mono font-bold text-xl shadow-sm border border-[#D4AF37]/30">
              {brand.brand_code}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider">
                  DATABASE MASTER NO. BATCH
                </span>
                <Badge className="bg-[#D4AF37] text-[#302621] font-bold text-[10px] px-2 py-0.5 shadow-2xs">
                  {allVariants.length} Varian Terdaftar
                </Badge>
                {brand.is_legacy_verified ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-full shadow-2xs">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span>Sesuai Batch Lama</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100/60 text-[#8C6208] border border-amber-300/70 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                    <span>Belum Dicek</span>
                  </span>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-[#302621] tracking-tight mt-0.5">
                {brand.brand_name}
              </h2>
            </div>
          </div>

          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="w-10 h-10 rounded-2xl bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 border border-[#E6D5B8] shadow-2xs cursor-pointer"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Toolbar & Missing Variants Notification */}
        <div className="p-4 sm:p-5 bg-[#FDFBF7] border-b border-[#E6D5B8]/60 space-y-3 shrink-0">
          {missingFromBatch.length > 0 ? (
            <div className="bg-amber-50/90 border border-amber-300/90 rounded-2xl p-4 shadow-2xs">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2.5 flex-1 min-w-[280px]">
                  <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-900 leading-tight">
                      Ditemukan <span className="underline decoration-amber-500 font-black">{missingFromBatch.length} Varian</span> di Database Resep {brand.brand_name} yang Belum Masuk ke Nomor Batch:
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Klik nama varian di bawah untuk langsung mendaftarkannya secara berurutan:
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2.5 max-h-28 overflow-y-auto pr-1">
                      {missingFromBatch.map((varName, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleAddMissing(varName)}
                          className="inline-flex items-center gap-1 bg-white hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-lg px-2.5 py-1 text-xs font-bold transition-all cursor-pointer group shadow-2xs"
                        >
                          <Plus className="w-3 h-3 text-amber-600 group-hover:scale-125 transition-transform" />
                          <span>{varName}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  size="sm"
                  type="button"
                  onClick={handleAddAll}
                  className="bg-[#8C6208] hover:bg-[#725007] text-white text-xs font-bold px-4 py-2 h-auto rounded-xl shadow-sm shrink-0 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Tambahkan Semua ({missingFromBatch.length} Varian)
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl px-3.5 py-2 text-xs text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span><b>Lengkap 100%!</b> Seluruh varian dari Database Resep {brand.brand_name} sudah terdaftar di Master Nomor Batch.</span>
            </div>
          )}

          {/* Search & Add Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cari nama varian atau no. kode (#01, #17, Acua Kiss...)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 text-xs bg-white border-[#E6D5B8] rounded-xl focus-visible:ring-[#8C6208]"
              />
              {search && (
                <button 
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-1 sm:flex-initial">
              <Input
                placeholder={`Nama Varian Baru (No. ${allVariants.length + 1})...`}
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddCustom();
                }}
                className="h-10 text-xs bg-white border-[#E6D5B8] rounded-xl min-w-[220px]"
              />
              <Button
                type="button"
                onClick={handleAddCustom}
                disabled={!newVarName.trim()}
                className="h-10 px-4 bg-[#8C6208] hover:bg-[#725007] text-white text-xs font-bold rounded-xl shrink-0 cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah
              </Button>
            </div>
          </div>
        </div>

        {/* Modal Body: Spacious Responsive Grid (3 Columns) */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-slate-50/50">
          {filteredIndexedVariants.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
              <p className="text-sm font-bold text-slate-600">Tidak ada varian yang sesuai pencarian</p>
              <p className="text-xs text-slate-400 mt-1">Coba gunakan kata kunci lain atau bersihkan kotak pencarian.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {filteredIndexedVariants.map((v) => {
                const isEditingThis = editingVar?.index === v._index;
                return (
                  <div 
                    key={v._index}
                    className="bg-white border border-slate-200/80 hover:border-[#8C6208]/50 rounded-2xl p-3.5 shadow-2xs hover:shadow-sm transition-all flex items-center justify-between gap-3 group"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="px-2 py-1 rounded-lg bg-[#FDFBF7] text-[#8C6208] border border-[#E6D5B8] flex items-center gap-1 font-mono font-bold text-xs shrink-0 shadow-2xs">
                        <span className="text-[10px] text-[#D4AF37]">✦</span>
                        <span>{v._code}</span>
                      </span>
                      {isEditingThis ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input
                            value={editingVar.name}
                            onChange={(e) => setEditingVar({ ...editingVar, name: e.target.value })}
                            className="h-8 text-xs bg-white"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') setEditingVar(null);
                            }}
                          />
                          <Button size="icon" type="button" className="w-8 h-8 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" onClick={handleSaveEdit}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" type="button" variant="ghost" className="w-8 h-8 text-slate-400 shrink-0" onClick={() => setEditingVar(null)}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-xs text-[#302621] truncate">
                            {v.name}
                          </p>
                          <p className="text-[10px] font-mono text-[#8C6208] mt-0.5">
                            Batch: {brand.brand_code}{v._code}0126I01
                          </p>
                        </div>
                      )}
                    </div>

                    {!isEditingThis && (
                      <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100">
                        <button 
                          type="button"
                          onClick={() => setEditingVar({ index: v._index, name: v.name })}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-[#8C6208] hover:bg-amber-50 cursor-pointer"
                          title="Edit nama varian"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleDelete(v._index)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                          title="Hapus varian"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-[#F9F5EC] border-t border-[#E6D5B8] flex items-center justify-between gap-3 shrink-0">
          <span className="text-xs font-semibold text-[#7A695B]">
            Menampilkan <b>{filteredIndexedVariants.length}</b> dari total <b>{allVariants.length}</b> Varian {brand.brand_name}
          </span>
          <Button
            type="button"
            onClick={onClose}
            className="bg-[#302621] hover:bg-[#201915] text-white text-xs font-bold px-6 h-9 rounded-xl cursor-pointer shadow-xs"
          >
            Selesai / Tutup
          </Button>
        </div>
      </div>
    </div>
  );
});


// --- LUXURY SEARCHABLE SELECT COMPONENTS (SATALA MONOCHROME GOLD & DARK) ---
const SearchableBrandSelect = ({ 
  batchMasters = [], 
  value = '', 
  onChange, 
  placeholder = '-- Pilih Brand --' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const selectedBrand = useMemo(() => {
    return batchMasters.find(b => b.brand_name === value);
  }, [batchMasters, value]);

  const filteredBrands = useMemo(() => {
    if (!search.trim()) return batchMasters;
    const q = search.toLowerCase();
    return batchMasters.filter(b => 
      (b.brand_name || '').toLowerCase().includes(q) ||
      (b.brand_code || '').toLowerCase().includes(q)
    );
  }, [batchMasters, search]);

  const handleSelect = (brandName) => {
    onChange(brandName);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className={`w-full h-10 px-3 text-xs bg-white border rounded-xl flex items-center justify-between gap-2 transition-all cursor-pointer shadow-2xs ${
          isOpen 
            ? 'border-[#8C6208] ring-2 ring-[#8C6208]/20' 
            : 'border-[#E6D5B8] hover:border-[#8C6208]/60'
        }`}
      >
        <div className="flex items-center gap-2 truncate flex-1 text-left">
          {selectedBrand ? (
            <>
              <span className="font-bold text-[#302621] truncate">
                {selectedBrand.brand_name}
              </span>
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#302621] text-[#D4AF37] shrink-0 shadow-2xs">
                {selectedBrand.brand_code}
              </span>
            </>
          ) : (
            <span className="text-slate-400 font-normal">
              {placeholder}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Reset Brand"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-[#7A695B] transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#8C6208]' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-full min-w-[280px] sm:min-w-[340px] bg-white rounded-2xl border border-[#E6D5B8] shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          <div className="p-2.5 bg-[#FDFBF7] border-b border-[#E6D5B8]/80 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama atau kode brand..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-[#E6D5B8] rounded-xl outline-none focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208]"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                  if (e.key === 'Enter' && filteredBrands.length > 0) {
                    handleSelect(filteredBrands[0].brand_name);
                  }
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between text-[10px] text-[#7A695B] px-1">
              <span className="font-semibold">
                Ditemukan {filteredBrands.length} dari {batchMasters.length} Brand
              </span>
              <span className="font-mono text-[#8C6208]">
                Master Database
              </span>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto p-1.5 space-y-1 overscroll-contain">
            {filteredBrands.length === 0 ? (
              <div className="text-center py-8 px-4">
                <p className="text-xs font-bold text-slate-600">Brand tidak ditemukan</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Coba gunakan kata kunci atau kode singkatan lain.</p>
              </div>
            ) : (
              filteredBrands.map((b) => {
                const isSelected = b.brand_name === value;
                const varCount = (b.variants || []).length;

                return (
                  <button
                    key={b.id || b.brand_code}
                    type="button"
                    onClick={() => handleSelect(b.brand_name)}
                    className={`w-full px-3 py-2 text-xs rounded-xl flex items-center justify-between gap-2.5 transition-all text-left cursor-pointer ${
                      isSelected
                        ? 'bg-[#F9F5EC] border border-[#E6D5B8] text-[#8C6208] shadow-2xs font-bold'
                        : 'hover:bg-amber-50/70 text-[#302621] border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate flex-1">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelected ? 'bg-[#8C6208]' : 'bg-slate-300'}`} />
                      <div className="truncate">
                        <span className="font-bold block truncate">{b.brand_name}</span>
                        <span className="text-[10px] font-normal text-slate-500 block">
                          {varCount} varian terdaftar
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#302621] text-[#D4AF37] shadow-2xs">
                        {b.brand_code}
                      </span>
                      {isSelected && (
                        <Check className="w-3.5 h-3.5 text-[#8C6208] stroke-[2.5]" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SearchableVariantSelect = ({
  variants = [],
  value = '',
  onChange,
  disabled = false,
  placeholder = '-- Pilih Varian --'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const selectedVariant = useMemo(() => {
    return variants.find(v => v.name === value);
  }, [variants, value]);

  const filteredVariants = useMemo(() => {
    if (!search.trim()) return variants;
    const q = search.toLowerCase();
    return variants.filter(v => 
      (v.name || '').toLowerCase().includes(q) ||
      (v.code || '').toLowerCase().includes(q)
    );
  }, [variants, search]);

  const handleSelect = (variantName) => {
    onChange(variantName);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        className={`w-full h-10 px-3 text-xs bg-white border rounded-xl flex items-center justify-between gap-2 transition-all shadow-2xs ${
          disabled
            ? 'opacity-50 bg-slate-100/70 border-slate-200 cursor-not-allowed text-slate-400'
            : isOpen 
              ? 'border-[#8C6208] ring-2 ring-[#8C6208]/20 cursor-pointer' 
              : 'border-[#E6D5B8] hover:border-[#8C6208]/60 cursor-pointer'
        }`}
      >
        <div className="flex items-center gap-2 truncate flex-1 text-left">
          {disabled ? (
            <span className="text-slate-400 italic">
              Pilih Brand terlebih dahulu
            </span>
          ) : selectedVariant ? (
            <>
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F9F5EC] border border-[#E6D5B8] text-[#8C6208] shrink-0">
                #{selectedVariant.code}
              </span>
              <span className="font-bold text-[#302621] truncate">
                {selectedVariant.name}
              </span>
            </>
          ) : (
            <span className="text-slate-400 font-normal">
              {placeholder}
            </span>
          )}
        </div>

        {!disabled && (
          <div className="flex items-center gap-1.5 shrink-0">
            {value && (
              <span
                onClick={handleClear}
                className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Reset Varian"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className={`w-4 h-4 text-[#7A695B] transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#8C6208]' : ''}`} />
          </div>
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 top-full mt-1.5 w-full min-w-[260px] sm:min-w-[320px] bg-white rounded-2xl border border-[#E6D5B8] shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          <div className="p-2.5 bg-[#FDFBF7] border-b border-[#E6D5B8]/80 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama atau nomor varian..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-[#E6D5B8] rounded-xl outline-none focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208]"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                  if (e.key === 'Enter' && filteredVariants.length > 0) {
                    handleSelect(filteredVariants[0].name);
                  }
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between text-[10px] text-[#7A695B] px-1">
              <span className="font-semibold">
                Ditemukan {filteredVariants.length} dari {variants.length} Varian
              </span>
              <span className="font-mono text-[#8C6208]">
                Urutan Kronologis
              </span>
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto p-1.5 space-y-1 overscroll-contain">
            {variants.length === 0 ? (
              <div className="text-center py-6 px-4">
                <p className="text-xs font-bold text-slate-600">Belum ada varian terdaftar</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Daftarkan varian di tab 'Daftar Brand & Nomor Varian'.</p>
              </div>
            ) : filteredVariants.length === 0 ? (
              <div className="text-center py-6 px-4">
                <p className="text-xs font-bold text-slate-600">Varian tidak ditemukan</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Coba kata kunci pencarian lainnya.</p>
              </div>
            ) : (
              filteredVariants.map((v, idx) => {
                const isSelected = v.name === value;
                const vCode = v.code || String(idx + 1).padStart(2, '0');

                return (
                  <button
                    key={vCode || idx}
                    type="button"
                    onClick={() => handleSelect(v.name)}
                    className={`w-full px-3 py-2 text-xs rounded-xl flex items-center justify-between gap-2.5 transition-all text-left cursor-pointer ${
                      isSelected
                        ? 'bg-[#F9F5EC] border border-[#E6D5B8] text-[#8C6208] shadow-2xs font-bold'
                        : 'hover:bg-amber-50/70 text-[#302621] border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate flex-1">
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FDFBF7] border border-[#E6D5B8] text-[#8C6208] shrink-0">
                        #{vCode}
                      </span>
                      <span className="font-bold truncate text-[#302621]">{v.name}</span>
                    </div>

                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-[#8C6208] stroke-[2.5] shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function WorkspaceBatchDatabase() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('master'); // 'master' | 'history' | 'simulator'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortByBrand, setSortByBrand] = useState('az'); // 'az' | 'za' | 'newest' | 'oldest' | 'most_variants'

  // Full Screen / Expanded Modal State
  const [expandedBrandId, setExpandedBrandId] = useState(null);
  
  // Modal States
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [newBrandForm, setNewBrandForm] = useState({ brand_name: '', brand_code: '' });
  
  const [addingVariantForBrandId, setAddingVariantForBrandId] = useState(null);
  const [newVariantName, setNewVariantName] = useState('');

  const [editingVariant, setEditingVariant] = useState(null); // { brandId, index, name }

  // Verification Filter State ('all' | 'verified' | 'unverified')
  const [filterVerification, setFilterVerification] = useState('all');

  // Simulator States
  const [simBrand, setSimBrand] = useState('');
  const [simVariant, setSimVariant] = useState('');
  const [simDate, setSimDate] = useState(new Date().toISOString().slice(0, 10));
  const [simCount, setSimCount] = useState(1);

  // 1. Query Master Batch Brands & Variants
  const { data: batchMasters = [], isLoading: isLoadingMaster } = useQuery({
    queryKey: ['batch-masters'],
    queryFn: fetchBatchMasters,
  });

  // 2. Query Batch History Logs
  const { data: batchHistories = [], isLoading: isLoadingHistory } = useQuery({
    queryKey: ['batch-histories'],
    queryFn: fetchBatchHistories,
  });

  // 3. Query RacikanDatabase to detect missing variants
  const { data: allRacikanDb = [] } = useQuery({
    queryKey: ['racikan-database-for-batch-check'],
    queryFn: () => base44.entities.RacikanDatabase.list().catch(() => [])
  });

  // Hitung jumlah brand terverifikasi vs belum diverifikasi
  const verifiedCount = useMemo(() => batchMasters.filter(b => b.is_legacy_verified).length, [batchMasters]);
  const unverifiedCount = batchMasters.length - verifiedCount;

  // Toggle Cepat Verifikasi Status Sesuai Batch Lama
  const handleToggleVerification = async (brand) => {
    const nextVal = !brand.is_legacy_verified;
    const updatedList = batchMasters.map(b => {
      if (b.id === brand.id) {
        return {
          ...b,
          is_legacy_verified: nextVal
        };
      }
      return b;
    });
    try {
      await saveBatchMasterList(updatedList);
      queryClient.invalidateQueries({ queryKey: ['batch-masters'] });
      if (nextVal) {
        toast.success(`✓ "${brand.brand_name}" ditandai: Sudah Sesuai Format Batch Lama`);
      } else {
        toast.info(`Status verifikasi "${brand.brand_name}" direset ke Belum Dicek`);
      }
    } catch (e) {
      toast.error('Gagal memperbarui status verifikasi: ' + e.message);
    }
  };

  // Helper menampilkan Bulan & Tahun batch
  const getBatchMonthDisplay = (h) => {
    const dateSource = h.spk_date || h.date || h.printed_at;
    if (dateSource) {
      try {
        const d = new Date(dateSource);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' });
        }
      } catch {
        // continue
      }
    }
    const batchStr = h.batch_code || h.batch_number || '';
    if (batchStr && batchStr.includes('-')) {
      const parts = batchStr.split('-');
      if (parts.length >= 3) {
        const mmyy = parts[2];
        if (mmyy && mmyy.length === 4) {
          const mStr = mmyy.substring(0, 2);
          const yStr = '20' + mmyy.substring(2, 4);
          const monthIdx = parseInt(mStr, 10) - 1;
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
          if (monthIdx >= 0 && monthIdx < 12) {
            return `${monthNames[monthIdx]} ${yStr}`;
          }
        }
      }
    }
    return '';
  };

  // Helper mendapatkan nomor urut batch (01, 02, ...)
  const getBatchIndexDisplay = (h) => {
    if (h.batch_index_monthly) {
      const num = Number(h.batch_index_monthly);
      return num < 10 ? `0${num}` : `${num}`;
    }
    const batchStr = h.batch_code || h.batch_number || '';
    if (batchStr && batchStr.includes('-')) {
      const parts = batchStr.split('-');
      const lastPart = parts[parts.length - 1];
      if (lastPart && !isNaN(parseInt(lastPart, 10))) {
        const num = parseInt(lastPart, 10);
        return num < 10 ? `0${num}` : `${num}`;
      }
    }
    return '01';
  };

  // Helper format tanggal SPK
  const formatSpkDate = (dateVal) => {
    if (!dateVal || dateVal === '-') return '-';
    try {
      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
      }
    } catch {}
    return dateVal;
  };

  // Mutations
  const createBrandMutation = useMutation({
    mutationFn: async (payload) => {
      const code = formatBrandCode(payload.brand_name, payload.brand_code);
      const collision = checkBrandCodeCollision(code, batchMasters);
      if (collision.isConflict) {
        throw new Error(`Kode "${code}" sudah digunakan oleh Brand "${collision.conflictBrand.brand_name}". Harap gunakan kode unik lain.`);
      }
      const newBrand = {
        id: `batch_master_${payload.brand_name.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`,
        brand_name: payload.brand_name.trim(),
        brand_code: code,
        variants: [],
        is_legacy_verified: !!payload.is_legacy_verified
      };
      const updatedList = [newBrand, ...batchMasters];
      return await saveBatchMasterList(updatedList);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-masters'] });
      toast.success('Brand baru berhasil didaftarkan ke Database Batch!');
      setShowAddBrandModal(false);
      setNewBrandForm({ brand_name: '', brand_code: '' });
    },
    onError: (err) => toast.error(err.message || 'Gagal mendaftarkan brand')
  });

  const updateBrandMutation = useMutation({
    mutationFn: async ({ id, brand_name, brand_code, variants, is_legacy_verified }) => {
      const code = formatBrandCode(brand_name, brand_code);
      const collision = checkBrandCodeCollision(code, batchMasters, id);
      if (collision.isConflict) {
        throw new Error(`Kode "${code}" sudah digunakan oleh Brand "${collision.conflictBrand.brand_name}".`);
      }
      const updatedList = batchMasters.map(b => {
        if (b.id === id) {
          return {
            ...b,
            brand_name: brand_name.trim(),
            brand_code: code,
            variants: variants || b.variants || [],
            is_legacy_verified: typeof is_legacy_verified === 'boolean' ? is_legacy_verified : !!b.is_legacy_verified
          };
        }
        return b;
      });
      return await saveBatchMasterList(updatedList);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-masters'] });
      toast.success('Data Brand berhasil diperbarui!');
      setEditingBrand(null);
    },
    onError: (err) => toast.error(err.message || 'Gagal memperbarui brand')
  });

  const deleteBrandMutation = useMutation({
    mutationFn: async (id) => {
      const updatedList = batchMasters.filter(b => b.id !== id);
      return await saveBatchMasterList(updatedList);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-masters'] });
      toast.success('🗑️ Brand berhasil dihapus dari database.');
    }
  });

  // Handler: Tambah Varian Baru (Urutan selalu bertambah / Chronological)
  const handleAddVariant = async (brand) => {
    if (!newVariantName.trim()) {
      toast.error('Masukkan nama varian terlebih dahulu.');
      return;
    }

    const currentVariants = brand.variants || [];
    const nextIndex = currentVariants.length + 1;
    const nextCode = String(nextIndex).padStart(2, '0');

    const updatedVariants = [
      ...currentVariants,
      {
        code: nextCode,
        name: newVariantName.trim(),
        registered_date: new Date().toISOString().slice(0, 10)
      }
    ];

    try {
      await updateBrandMutation.mutateAsync({
        id: brand.id,
        brand_name: brand.brand_name,
        brand_code: brand.brand_code,
        variants: updatedVariants
      });
      setAddingVariantForBrandId(null);
      setNewVariantName('');
      toast.success(`✅ Varian "${newVariantName.trim()}" terdaftar sebagai No Urut ${nextCode}`);
    } catch (e) {
      toast.error('Gagal menambahkan varian: ' + e.message);
    }
  };

  // Handler: Update Nama Varian
  const handleSaveEditVariant = async (brand) => {
    if (!editingVariant || !editingVariant.name.trim()) return;

    const updatedVariants = [...(brand.variants || [])];
    if (updatedVariants[editingVariant.index]) {
      updatedVariants[editingVariant.index] = {
        ...updatedVariants[editingVariant.index],
        name: editingVariant.name.trim()
      };

      await updateBrandMutation.mutateAsync({
        id: brand.id,
        brand_name: brand.brand_name,
        brand_code: brand.brand_code,
        variants: updatedVariants
      });
      setEditingVariant(null);
    }
  };

  // Handler: Hapus Varian (dan rapikan nomor urut jika diperlukan)
  const handleDeleteVariant = async (brand, variantIdx) => {
    const varName = brand.variants?.[variantIdx]?.name || 'Varian';
    if (!window.confirm(`Yakin ingin menghapus varian "${varName}"? Nomor urut varian di bawahnya akan disesuaikan secara berurutan.`)) return;

    const remaining = (brand.variants || []).filter((_, i) => i !== variantIdx);
    // Renumber strictly 01, 02, 03
    const renumbered = remaining.map((v, i) => ({
      ...v,
      code: String(i + 1).padStart(2, '0')
    }));

    await updateBrandMutation.mutateAsync({
      id: brand.id,
      brand_name: brand.brand_name,
      brand_code: brand.brand_code,
      variants: renumbered
    });
  };

  // Filter & Sort Brand (Urutan varian di dalam kartu tetap dipertahankan secara kronologis)
  const filteredBrands = useMemo(() => {
    let list = [...batchMasters];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(b => 
        b.brand_name.toLowerCase().includes(q) ||
        b.brand_code.toLowerCase().includes(q) ||
        (b.variants || []).some(v => v.name.toLowerCase().includes(q))
      );
    }

    // Filter Status Verifikasi Kesesuaian Batch Lama
    if (filterVerification === 'verified') {
      list = list.filter(b => b.is_legacy_verified);
    } else if (filterVerification === 'unverified') {
      list = list.filter(b => !b.is_legacy_verified);
    }

    // Sorting Kartu Brand
    if (sortByBrand === 'az') {
      list.sort((a, b) => (a.brand_name || '').localeCompare(b.brand_name || '', 'id', { sensitivity: 'base' }));
    } else if (sortByBrand === 'za') {
      list.sort((a, b) => (b.brand_name || '').localeCompare(a.brand_name || '', 'id', { sensitivity: 'base' }));
    } else if (sortByBrand === 'newest') {
      list.reverse();
    } else if (sortByBrand === 'most_variants') {
      list.sort((a, b) => (b.variants?.length || 0) - (a.variants?.length || 0));
    }
    // 'oldest' preserves the default spreadsheet sequence

    return list;
  }, [batchMasters, searchQuery, filterVerification, sortByBrand]);

  // Statistik Total
  const totalVariants = useMemo(() => {
    return batchMasters.reduce((acc, b) => acc + (b.variants?.length || 0), 0);
  }, [batchMasters]);

  // Live Simulator Preview
  const simulatorResult = useMemo(() => {
    const selectedBrandObj = batchMasters.find(b => b.brand_name === simBrand);
    const brandCode = selectedBrandObj?.brand_code || (simBrand ? formatBrandCode(simBrand) : 'ARTHEA');
    
    let variantIndex = 1;
    if (selectedBrandObj && simVariant) {
      const vIdx = (selectedBrandObj.variants || []).findIndex(v => v.name === simVariant);
      if (vIdx !== -1) variantIndex = vIdx + 1;
    }

    const d = new Date(simDate || new Date());
    const day = String(d.getDate()).padStart(2, '0');
    const year2 = String(d.getFullYear()).slice(-2);
    const monthL = getMonthLetter(d);
    const count2 = String(simCount || 1).padStart(2, '0');
    const code = generateBatchCode({
      brandCode,
      variantIndex,
      dateStr: simDate,
      monthlyCount: simCount
    });

    return {
      code,
      brandCode,
      variantIndex: String(variantIndex).padStart(2, '0'),
      day,
      year2,
      monthL,
      monthNum: d.getMonth() + 1,
      count2
    };
  }, [simBrand, simVariant, simDate, simCount, batchMasters]);

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-[#302621] to-[#4A3B32] p-6 rounded-2xl text-white shadow-md">
        <div>
          <div className="flex items-center gap-2 text-[#D4AF37] font-semibold text-xs uppercase tracking-widest mb-1">
            <Layers className="w-4 h-4" /> Workspace Admin • Master Database
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            Database No. Batch & Standar SPK
          </h1>
          <p className="text-xs text-white/70 mt-1 max-w-2xl">
            Pusat acuan nomor urut varian BPOM, singkatan kode brand resmi (maks. 6 huruf), dan formula generator nomor batch untuk cetak SPK.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setShowAddBrandModal(true)}
            className="bg-[#D4AF37] hover:bg-[#b89528] text-[#302621] font-bold text-xs h-10 px-4 rounded-xl shadow-lg shadow-black/20 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Daftarkan Brand Baru
          </Button>
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-amber-50/90 border border-amber-200/60 text-[#8C6208] flex items-center justify-center font-bold text-lg shadow-2xs shrink-0">
            <Building2 className="w-5 h-5 text-[#8C6208]" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-[#7A695B] uppercase tracking-wider">Total Brand Master</div>
            <div className="text-xl font-black text-[#302621] mt-0.5">{batchMasters.length} Brand</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-emerald-50/90 border border-emerald-200/60 text-emerald-700 flex items-center justify-center font-bold text-lg shadow-2xs shrink-0">
            <Sparkles className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-[#7A695B] uppercase tracking-wider">Total Varian Terdaftar</div>
            <div className="text-xl font-black text-[#302621] mt-0.5">{totalVariants} Varian</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-blue-50/90 border border-blue-200/60 text-blue-700 flex items-center justify-center font-bold text-lg shadow-2xs shrink-0">
            <Hash className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-[#7A695B] uppercase tracking-wider">Batch Diterbitkan</div>
            <div className="text-xl font-black text-[#302621] mt-0.5">{batchHistories.length} Batch</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-[#E6D5B8] gap-2">
        <button
          onClick={() => setActiveTab('master')}
          className={`pb-3 px-4 font-bold text-xs md:text-sm transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'master'
              ? 'border-[#8C6208] text-[#8C6208]'
              : 'border-transparent text-[#7A695B] hover:text-[#302621]'
          }`}
        >
          <Layers className="w-4 h-4" />
          Daftar Brand & Nomor Varian ({batchMasters.length})
        </button>

        <button
          onClick={() => setActiveTab('simulator')}
          className={`pb-3 px-4 font-bold text-xs md:text-sm transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'simulator'
              ? 'border-[#8C6208] text-[#8C6208]'
              : 'border-transparent text-[#7A695B] hover:text-[#302621]'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Simulasi Formula No Batch
        </button>

        <button
          onClick={() => setActiveTab('history')}
          className={`pb-3 px-4 font-bold text-xs md:text-sm transition-all border-b-2 flex items-center gap-2 ${
            activeTab === 'history'
              ? 'border-[#8C6208] text-[#8C6208]'
              : 'border-transparent text-[#7A695B] hover:text-[#302621]'
          }`}
        >
          <FileText className="w-4 h-4" />
          Riwayat Batch Diterbitkan ({batchHistories.length})
        </button>
      </div>

      {/* TAB 1: MASTER BRAND & VARIAN (SPREADSHEET STYLE GRID) */}
      {activeTab === 'master' && (
        <div className="space-y-4">
          {/* Search, Sort & Notice */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5 bg-amber-50/60 border border-amber-200/80 p-3 rounded-xl text-xs text-[#8C6208]">
              <div className="w-6 h-6 rounded-lg bg-amber-100 border border-amber-300/60 text-[#8C6208] flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" />
              </div>
              <p className="leading-relaxed">
                <strong>Aturan Urutan Varian:</strong> Nomor urut (01, 02, ...) disusun kronologis sesuai tanggal pendaftaran/produksi pertama (tidak di-sort berdasarkan abjad). Varian baru otomatis menempati nomor berikutnya.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-[#E6D5B8] shadow-2xs">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A695B]" />
                  <Input
                    placeholder="Cari nama brand atau varian..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-9 text-xs border-[#E6D5B8] bg-slate-50/50 rounded-xl focus-visible:ring-[#8C6208]"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Filter Status Kesesuaian Batch Lama */}
                <div className="flex items-center gap-1 p-1 bg-slate-100/90 rounded-xl border border-slate-200/80 shrink-0">
                  <button
                    type="button"
                    onClick={() => setFilterVerification('all')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      filterVerification === 'all'
                        ? 'bg-white text-[#302621] shadow-2xs'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    Semua ({batchMasters.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterVerification('verified')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1 cursor-pointer ${
                      filterVerification === 'verified'
                        ? 'bg-emerald-600 text-white shadow-2xs'
                        : 'text-emerald-700 hover:bg-emerald-50'
                    }`}
                    title="Tampilkan hanya brand yang sudah sesuai dengan format batch lama"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Sudah Sesuai ({verifiedCount})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterVerification('unverified')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1 cursor-pointer ${
                      filterVerification === 'unverified'
                        ? 'bg-amber-600 text-white shadow-2xs'
                        : 'text-amber-800 hover:bg-amber-50'
                    }`}
                    title="Tampilkan brand yang belum diverifikasi"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                    <span>Belum Dicek ({unverifiedCount})</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-[#E6D5B8]">
                  <ArrowUpDown className="w-3.5 h-3.5 text-[#8C6208]" />
                  <span className="text-[11px] font-bold text-[#7A695B] whitespace-nowrap">Urutkan:</span>
                  <select
                    value={sortByBrand}
                    onChange={(e) => setSortByBrand(e.target.value)}
                    className="text-xs font-semibold bg-transparent text-[#302621] outline-none cursor-pointer pr-1"
                  >
                    <option value="az">Abjad A – Z</option>
                    <option value="za">Abjad Z – A</option>
                    <option value="newest">Terbaru Diinput</option>
                    <option value="oldest">Urutan Spreadsheet</option>
                    <option value="most_variants">Varian Terbanyak</option>
                  </select>
                </div>

                <Badge variant="outline" className="h-9 px-3 text-xs border-[#E6D5B8] text-[#8C6208] bg-[#FDFBF7] font-bold shrink-0">
                  {filteredBrands.length} Ditampilkan
                </Badge>
              </div>
            </div>
          </div>

          {/* Grid Brand Columns */}
          {isLoadingMaster ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-[#E6D5B8]">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#8C6208] mb-2" />
              <p className="text-xs text-[#7A695B] font-medium">Memuat master database batch...</p>
            </div>
          ) : filteredBrands.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-[#E6D5B8]">
              <p className="text-sm font-bold text-[#8C6208]">Tidak ada Brand yang cocok</p>
              <p className="text-xs text-[#7A695B] mt-1">Coba sesuaikan kata kunci pencarian atau filter status.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredBrands.map((brand) => (
                <div 
                  key={brand.id}
                  className="bg-white border border-[#E6D5B8] rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-[#F9F5EC] to-[#F3EAD8] p-4 border-b border-[#E6D5B8]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider block">
                            BRAND RESMI
                          </span>
                          
                          {/* Tanda Status Kesesuaian Batch Lama */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleVerification(brand);
                            }}
                            title={brand.is_legacy_verified ? "Status: Sudah Sesuai Format Batch Lama (Klik untuk ubah)" : "Status: Belum Dicek (Klik untuk tandai sudah sesuai)"}
                            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md transition-all cursor-pointer shadow-2xs ${
                              brand.is_legacy_verified
                                ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300'
                                : 'bg-amber-50 hover:bg-amber-100 text-[#8C6208] border border-amber-200/80'
                            }`}
                          >
                            {brand.is_legacy_verified ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>Sesuai Batch Lama</span>
                              </>
                            ) : (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"></span>
                                <span>Belum Dicek</span>
                              </>
                            )}
                          </button>
                        </div>
                        <h3 className="text-base font-black text-[#302621] mt-1 leading-tight truncate" title={brand.brand_name}>
                          {brand.brand_name}
                        </h3>
                      </div>
                      
                      <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setExpandedBrandId(brand.id);
                            setExpandedSearchQuery('');
                            setExpandedNewVariantName('');
                          }}
                          className="h-7 text-[11px] font-bold text-[#8C6208] border-[#E6D5B8] bg-white hover:bg-amber-50 rounded-lg shadow-2xs flex items-center gap-1 cursor-pointer"
                          title="Buka tampilan tabel penuh dan cek varian yang belum masuk"
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                          <span>Mode Full</span>
                        </Button>
                        <Badge className="bg-[#302621] text-[#D4AF37] border-0 font-mono font-bold text-[11px] px-2 py-0.5 rounded-lg shadow-2xs">
                          {brand.brand_code}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => setEditingBrand(brand)}
                          className="w-7 h-7 text-[#7A695B] hover:text-[#302621] cursor-pointer"
                          title="Edit Kode / Nama Brand"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Card Body: Variant List (Strict Order) */}
                  <div className="p-4 flex-1">
                    <div className="flex items-center justify-between text-[11px] font-bold text-[#7A695B] pb-2 border-b border-slate-100 mb-2 uppercase tracking-wider">
                      <span>Daftar Varian</span>
                      <span>{(brand.variants || []).length} Varian</span>
                    </div>

                    {(brand.variants || []).length === 0 ? (
                      <div className="py-4 text-center text-xs text-slate-400 italic">
                        Belum ada varian terdaftar
                      </div>
                    ) : (
                      <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                        {(brand.variants || []).map((v, vIdx) => {
                          const isEditingThis = editingVariant?.brandId === brand.id && editingVariant?.index === vIdx;
                          return (
                            <div 
                              key={vIdx}
                              className="flex items-center justify-between p-2 rounded-xl bg-slate-50 hover:bg-amber-50/50 border border-slate-100 text-xs transition-colors group"
                            >
                              <div className="flex items-center gap-2.5 flex-1 min-w-0 pr-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#F9F5EC] border border-[#E6D5B8] text-[#8C6208] font-mono font-bold text-[11px] shrink-0 shadow-2xs group-hover:border-[#D4AF37] transition-colors">
                                  <span className="text-[9px] text-[#D4AF37]">✦</span>
                                  <span>{v.code || String(vIdx + 1).padStart(2, '0')}</span>
                                </span>
                                {isEditingThis ? (
                                  <div className="flex items-center gap-1 flex-1">
                                    <Input
                                      value={editingVariant.name}
                                      onChange={(e) => setEditingVariant({ ...editingVariant, name: e.target.value })}
                                      className="h-7 text-xs bg-white"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveEditVariant(brand);
                                        if (e.key === 'Escape') setEditingVariant(null);
                                      }}
                                    />
                                    <Button size="icon" className="w-7 h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleSaveEditVariant(brand)}>
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button size="icon" variant="ghost" className="w-7 h-7 text-slate-400" onClick={() => setEditingVariant(null)}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="font-semibold text-[#302621] truncate">
                                    {v.name}
                                  </span>
                                )}
                              </div>

                              {!isEditingThis && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button 
                                    onClick={() => setEditingVariant({ brandId: brand.id, index: vIdx, name: v.name })}
                                    className="p-1 text-slate-400 hover:text-[#8C6208]"
                                    title="Edit nama varian"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteVariant(brand, vIdx)}
                                    className="p-1 text-slate-400 hover:text-red-600"
                                    title="Hapus varian"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Card Footer: Add Variant */}
                  <div className="p-3 bg-[#FDFBF7] border-t border-[#E6D5B8]/60">
                    {addingVariantForBrandId === brand.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          placeholder={`Nama Varian ke-${(brand.variants || []).length + 1}...`}
                          value={newVariantName}
                          onChange={(e) => setNewVariantName(e.target.value)}
                          className="h-8 text-xs bg-white border-[#E6D5B8]"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddVariant(brand);
                            if (e.key === 'Escape') {
                              setAddingVariantForBrandId(null);
                              setNewVariantName('');
                            }
                          }}
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleAddVariant(brand)}
                          className="h-8 bg-[#8C6208] hover:bg-[#6e4c06] text-white text-xs px-3 font-bold"
                        >
                          Simpan
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setAddingVariantForBrandId(null); setNewVariantName(''); }}
                          className="h-8 text-xs text-[#7A695B]"
                        >
                          Batal
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setAddingVariantForBrandId(brand.id);
                          setNewVariantName('');
                        }}
                        className="w-full py-1.5 px-3 rounded-xl border border-dashed border-[#D4AF37] text-[#8C6208] hover:bg-amber-50/60 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Tambah Varian Baru (No. {String((brand.variants || []).length + 1).padStart(2, '0')})
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: SIMULATOR NO BATCH */}
      {activeTab === 'simulator' && (
        <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h3 className="text-base font-bold text-[#302621] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8C6208]" />
              Simulator Generator Nomor Batch
            </h3>
            <p className="text-xs text-[#7A695B] mt-1">
              Gunakan simulator ini untuk menguji bagaimana formula penomoran batch dibuat secara otomatis sesuai standar yang telah disepakati.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <div className="relative z-30">
              <label className="text-xs font-bold text-[#7A695B] mb-1.5 block">1. Pilih Brand</label>
              <SearchableBrandSelect
                batchMasters={batchMasters}
                value={simBrand}
                onChange={(newBrand) => {
                  setSimBrand(newBrand);
                  const bObj = batchMasters.find(b => b.brand_name === newBrand);
                  if (bObj && bObj.variants && bObj.variants.length > 0) {
                    setSimVariant(bObj.variants[0].name);
                  } else {
                    setSimVariant('');
                  }
                }}
              />
            </div>

            <div className="relative z-20">
              <label className="text-xs font-bold text-[#7A695B] mb-1.5 block">2. Pilih Varian</label>
              <SearchableVariantSelect
                variants={(batchMasters.find(b => b.brand_name === simBrand)?.variants) || []}
                value={simVariant}
                disabled={!simBrand}
                onChange={(newVariant) => setSimVariant(newVariant)}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-[#7A695B] mb-1.5 block">3. Tanggal Cetak SPK</label>
              <Input
                type="date"
                value={simDate}
                onChange={(e) => setSimDate(e.target.value)}
                className="h-10 text-xs bg-white border-[#E6D5B8] rounded-xl"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-[#7A695B] mb-1.5 block">4. Urutan Produksi Bulan Ini</label>
              <Input
                type="number"
                min="1"
                max="99"
                value={simCount}
                onChange={(e) => setSimCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                onBlur={() => {
                  if (!simCount || Number(simCount) < 1) {
                    setSimCount(1);
                  }
                }}
                className="h-10 text-xs bg-white border-[#E6D5B8] rounded-xl"
              />
            </div>
          </div>

          {/* Live Code Result Box */}
          <div className="bg-gradient-to-br from-[#302621] to-[#1A1411] text-white p-6 rounded-2xl shadow-xl">
            <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest block mb-1">
              HASIL GENERATOR NO BATCH OTOMATIS
            </span>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="font-mono text-2xl md:text-3xl font-black text-[#FFFDF9] tracking-wider bg-white/10 px-4 py-2 rounded-xl border border-white/10 w-fit">
                {simulatorResult.code}
              </div>
              <Badge className="bg-[#D4AF37] text-[#302621] font-bold text-xs py-1 px-3">
                Panjang: {simulatorResult.code.length} Karakter
              </Badge>
            </div>

            {/* Breakdown Detail Table */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-6 pt-4 border-t border-white/10 text-center">
              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">Kode Brand</span>
                <span className="font-mono text-base font-bold">{simulatorResult.brandCode}</span>
                <span className="text-[9px] text-white/50 block">Maks 6 Huruf</span>
              </div>

              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">No Varian</span>
                <span className="font-mono text-base font-bold">{simulatorResult.variantIndex}</span>
                <span className="text-[9px] text-white/50 block">Urutan Master</span>
              </div>

              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">Tanggal</span>
                <span className="font-mono text-base font-bold">{simulatorResult.day}</span>
                <span className="text-[9px] text-white/50 block">DD</span>
              </div>

              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">Tahun</span>
                <span className="font-mono text-base font-bold">{simulatorResult.year2}</span>
                <span className="text-[9px] text-white/50 block">YY (2026 $\rightarrow$ 26)</span>
              </div>

              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">Bulan Alfabet</span>
                <span className="font-mono text-base font-bold text-amber-300">{simulatorResult.monthL}</span>
                <span className="text-[9px] text-white/50 block">Bulan {simulatorResult.monthNum}</span>
              </div>

              <div className="bg-white/5 p-2 rounded-xl">
                <span className="text-[10px] text-[#D4AF37] block font-semibold">Counter Bulanan</span>
                <span className="font-mono text-base font-bold">{simulatorResult.count2}</span>
                <span className="text-[9px] text-white/50 block">Batch ke-{simCount}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: RIWAYAT BATCH DITERBITKAN */}
      {activeTab === 'history' && (
        <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-bold text-[#302621]">Riwayat Nomor Batch Diterbitkan</h3>
              <p className="text-xs text-[#7A695B]">Log batch yang telah resmi dicetak untuk dokumen SPK di produksi.</p>
            </div>
          </div>

          {batchHistories.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[#E6D5B8] rounded-xl">
              <p className="text-sm font-bold text-[#8C6208]">Belum Ada Batch Diterbitkan</p>
              <p className="text-xs text-[#7A695B] mt-1">Nomor batch akan otomatis tercatat di sini begitu tim mengklik tombol "Cetak SPK".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-[#F9F5EC] text-[#7A695B] font-bold uppercase tracking-wider border-b border-[#E6D5B8]">
                  <tr>
                    <th className="p-3">No. Batch</th>
                    <th className="p-3">Brand</th>
                    <th className="p-3">Varian</th>
                    <th className="p-3">Tgl SPK</th>
                    <th className="p-3">Batch Ke</th>
                    <th className="p-3">Dicetak Oleh</th>
                    <th className="p-3">Waktu Cetak</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {batchHistories.map((h, i) => {
                    const batchNum = h.batch_code || h.batch_number || h.no_batch || '-';
                    const spkDateVal = h.spk_date || h.date || (h.printed_at ? h.printed_at.split('T')[0] : '-');
                    const batchIdx = getBatchIndexDisplay(h);
                    const monthLabel = getBatchMonthDisplay(h);

                    return (
                      <tr key={h.id || i} className="hover:bg-slate-50">
                        <td className="p-3 font-mono font-bold text-[#8C6208]">{batchNum}</td>
                        <td className="p-3 font-bold text-[#302621]">{h.brand_name || '-'}</td>
                        <td className="p-3">{h.variant_name || '-'}</td>
                        <td className="p-3">{formatSpkDate(spkDateVal)}</td>
                        <td className="p-3 font-mono">
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="font-bold text-[#8C6208] bg-amber-50 border border-amber-200/70 px-1.5 py-0.5 rounded text-[11px]">
                              {batchIdx}
                            </span>
                            {monthLabel && (
                              <span className="text-[10px] text-slate-500 font-sans font-medium">
                                ({monthLabel})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">{h.printed_by || 'Admin'}</td>
                        <td className="p-3 text-slate-400">{h.printed_at ? new Date(h.printed_at).toLocaleString('id-ID') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL: DAFTARKAN BRAND BARU */}
      {showAddBrandModal && (() => {
        const collision = checkBrandCodeCollision(newBrandForm.brand_code, batchMasters);
        const suggestedCode = collision.isConflict ? generateUniqueBrandCode(newBrandForm.brand_name, '', batchMasters) : null;
        const isSaveDisabled = !newBrandForm.brand_name.trim() || !newBrandForm.brand_code.trim() || collision.isConflict || createBrandMutation.isPending;

        return (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E6D5B8] space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <h3 className="font-bold text-base text-[#302621] flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#8C6208]" /> Daftarkan Brand Baru
                </h3>
                <Button size="icon" variant="ghost" onClick={() => setShowAddBrandModal(false)} className="w-7 h-7 text-slate-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-bold text-[#7A695B] mb-1 block">Nama Lengkap Brand <span className="text-red-500">*</span></label>
                  <Input
                    placeholder="Contoh: OXYGAIN"
                    value={newBrandForm.brand_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      const uniqueCode = generateUniqueBrandCode(val, '', batchMasters);
                      setNewBrandForm({
                        brand_name: val,
                        brand_code: uniqueCode
                      });
                    }}
                    className="h-9 text-xs rounded-xl border-[#E6D5B8]"
                  />
                </div>

                <div>
                  <label className="font-bold text-[#7A695B] mb-1 block">Kode Singkatan (Maksimal 6 Huruf Kapital) <span className="text-red-500">*</span></label>
                  <Input
                    placeholder="Contoh: OXYG"
                    maxLength={6}
                    value={newBrandForm.brand_code}
                    onChange={(e) => setNewBrandForm({ ...newBrandForm, brand_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                    className={`h-9 text-xs font-mono font-bold rounded-xl ${collision.isConflict ? 'border-red-400 focus-visible:ring-red-400 bg-red-50/40 text-red-700' : 'border-[#E6D5B8]'}`}
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">Kode ini akan menjadi awalan nomor batch resmi di SPK dan wajib unik (tidak boleh ada yang sama).</span>
                </div>

                {collision.isConflict && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 space-y-1.5 animate-in fade-in">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold leading-tight">
                          Kode "{newBrandForm.brand_code}" sudah dipakai oleh Brand "{collision.conflictBrand?.brand_name}"!
                        </p>
                        <p className="text-[11px] text-red-600 mt-0.5">
                          Setiap brand harus memiliki kode batch yang berbeda agar batch BPOM & SPK tidak tertukar.
                        </p>
                      </div>
                    </div>
                    {suggestedCode && (
                      <div className="pt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-slate-600">Rekomendasi kode unik:</span>
                        <button
                          type="button"
                          onClick={() => setNewBrandForm({ ...newBrandForm, brand_code: suggestedCode })}
                          className="inline-flex items-center gap-1 font-mono font-black text-xs bg-white hover:bg-amber-50 text-[#8C6208] border border-amber-300 px-2 py-0.5 rounded-lg shadow-2xs cursor-pointer transition-colors"
                        >
                          <span>✦ Gunakan {suggestedCode}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Checkbox Status Verifikasi Kesesuaian Batch Lama */}
                <div 
                  onClick={() => setNewBrandForm(prev => ({ ...prev, is_legacy_verified: !prev.is_legacy_verified }))}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 select-none ${
                    newBrandForm.is_legacy_verified 
                      ? 'bg-emerald-50/80 border-emerald-300 text-emerald-900 shadow-2xs' 
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                    newBrandForm.is_legacy_verified ? 'bg-emerald-600 text-white' : 'border-2 border-slate-300 bg-white'
                  }`}>
                    {newBrandForm.is_legacy_verified && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 font-bold text-xs">
                      <span>Kode Singkatan Sudah Sesuai Format Batch Lama</span>
                      {newBrandForm.is_legacy_verified && (
                        <span className="text-[10px] bg-emerald-200/70 text-emerald-800 font-bold px-1.5 py-0.2 rounded">Terverifikasi</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      Tandai jika kode singkatan brand ini sudah cocok dengan acuan batch terdahulu.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <Button variant="ghost" size="sm" onClick={() => setShowAddBrandModal(false)} className="text-xs">
                  Batal
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => createBrandMutation.mutate(newBrandForm)}
                  disabled={isSaveDisabled}
                  className="bg-[#8C6208] hover:bg-[#6e4c06] text-white text-xs font-bold px-4 disabled:opacity-50"
                >
                  {createBrandMutation.isPending ? 'Mendaftarkan...' : 'Daftarkan Brand'}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL: EDIT BRAND */}
      {editingBrand && (() => {
        const collision = checkBrandCodeCollision(editingBrand.brand_code, batchMasters, editingBrand.id);
        const suggestedCode = collision.isConflict ? generateUniqueBrandCode(editingBrand.brand_name, '', batchMasters, editingBrand.id) : null;
        const isSaveDisabled = !editingBrand.brand_name.trim() || !editingBrand.brand_code.trim() || collision.isConflict || updateBrandMutation.isPending;

        return (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E6D5B8] space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <h3 className="font-bold text-base text-[#302621] flex items-center gap-2">
                  <Edit2 className="w-4 h-4 text-[#8C6208]" /> Edit Data Brand
                </h3>
                <Button size="icon" variant="ghost" onClick={() => setEditingBrand(null)} className="w-7 h-7 text-slate-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-bold text-[#7A695B] mb-1 block">Nama Brand</label>
                  <Input
                    value={editingBrand.brand_name}
                    onChange={(e) => setEditingBrand({ ...editingBrand, brand_name: e.target.value })}
                    className="h-9 text-xs rounded-xl border-[#E6D5B8]"
                  />
                </div>

                <div>
                  <label className="font-bold text-[#7A695B] mb-1 block">Kode Singkatan (Maks. 6 Karakter)</label>
                  <Input
                    maxLength={6}
                    value={editingBrand.brand_code}
                    onChange={(e) => setEditingBrand({ ...editingBrand, brand_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                    className={`h-9 text-xs font-mono font-bold rounded-xl ${collision.isConflict ? 'border-red-400 focus-visible:ring-red-400 bg-red-50/40 text-red-700' : 'border-[#E6D5B8]'}`}
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">Kode ini wajib unik di seluruh database nomor batch.</span>
                </div>

                {/* Checkbox Status Verifikasi Kesesuaian Batch Lama */}
                <div 
                  onClick={() => setEditingBrand(prev => ({ ...prev, is_legacy_verified: !prev.is_legacy_verified }))}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 select-none ${
                    editingBrand.is_legacy_verified 
                      ? 'bg-emerald-50/80 border-emerald-300 text-emerald-900 shadow-2xs' 
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                    editingBrand.is_legacy_verified ? 'bg-emerald-600 text-white' : 'border-2 border-slate-300 bg-white'
                  }`}>
                    {editingBrand.is_legacy_verified && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 font-bold text-xs">
                      <span>Kode Singkatan Sudah Sesuai Format Batch Lama</span>
                      {editingBrand.is_legacy_verified && (
                        <span className="text-[10px] bg-emerald-200/70 text-emerald-800 font-bold px-1.5 py-0.2 rounded">Terverifikasi</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      Tandai jika singkatan brand ini telah divalidasi dan cocok dengan standar penomoran batch terdahulu.
                    </p>
                  </div>
                </div>

                {collision.isConflict && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 space-y-1.5 animate-in fade-in">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold leading-tight">
                          Kode "{editingBrand.brand_code}" sudah dipakai oleh Brand "{collision.conflictBrand?.brand_name}"!
                        </p>
                        <p className="text-[11px] text-red-600 mt-0.5">
                          Ubah ke kode unik agar batch nomor SPK tidak bentrok.
                        </p>
                      </div>
                    </div>
                    {suggestedCode && (
                      <div className="pt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-slate-600">Rekomendasi kode unik:</span>
                        <button
                          type="button"
                          onClick={() => setEditingBrand({ ...editingBrand, brand_code: suggestedCode })}
                          className="inline-flex items-center gap-1 font-mono font-black text-xs bg-white hover:bg-amber-50 text-[#8C6208] border border-amber-300 px-2 py-0.5 rounded-lg shadow-2xs cursor-pointer transition-colors"
                        >
                          <span>✦ Gunakan {suggestedCode}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    if (window.confirm(`Hapus brand ${editingBrand.brand_name} beserta seluruh variannya?`)) {
                      deleteBrandMutation.mutate(editingBrand.id);
                      setEditingBrand(null);
                    }
                  }}
                  className="text-red-500 hover:text-red-700 text-xs"
                >
                  Hapus Brand
                </Button>

                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditingBrand(null)} className="text-xs">
                    Batal
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateBrandMutation.mutate(editingBrand)}
                    disabled={isSaveDisabled}
                    className="bg-[#8C6208] hover:bg-[#6e4c06] text-white text-xs font-bold px-4 disabled:opacity-50"
                  >
                    {updateBrandMutation.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL: EXPANDED FULL TABLE VIEW (1 BRAND ONLY - OPTIMIZED 60FPS) */}
      {expandedBrandId && (() => {
        const brand = batchMasters.find(b => b.id === expandedBrandId) || filteredBrands.find(b => b.id === expandedBrandId);
        if (!brand) return null;

        return (
          <ExpandedBrandModal
            brand={brand}
            allRacikanDb={allRacikanDb}
            onClose={() => setExpandedBrandId(null)}
            onUpdateVariants={async (brandId, updatedVariants) => {
              await updateBrandMutation.mutateAsync({
                id: brandId,
                brand_name: brand.brand_name,
                brand_code: brand.brand_code,
                variants: updatedVariants
              });
            }}
          />
        );
      })()}
    </div>
  );
}
